/**
 * Trunnion AI - Tour Step Component
 * Renders a spotlight overlay with a "hole" cut around the target element and a
 * positioned tooltip showing the step title, description, step counter, and
 * navigation controls. Styled in the Trunnion dark theme.
 */

import { useEffect, useState, useCallback, useRef, type CSSProperties } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { TourPlacement } from '@/config/tourSteps';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TourStepProps {
  /** CSS selector for the target element */
  target: string;
  /** Step heading */
  title: string;
  /** Step body text */
  description: string;
  /** Tooltip placement relative to the target */
  placement: TourPlacement;
  /** Current 0-based step index */
  stepIndex: number;
  /** Total number of steps */
  totalSteps: number;
  /** Whether a "Back" button should be shown (i.e. not the first step) */
  showBack: boolean;
  /** Called when the user clicks "Next" or "Finish" */
  onNext: () => void;
  /** Called when the user clicks "Back" */
  onBack: () => void;
  /** Called when the user clicks "Skip tour" */
  onSkip: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SPOTLIGHT_PADDING = 8;
const TOOLTIP_GAP = 12;
const TOOLTIP_WIDTH = 320;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getTargetRect(selector: string): DOMRect | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  return el.getBoundingClientRect();
}

function computeTooltipPosition(
  rect: DOMRect,
  placement: TourPlacement,
): CSSProperties {
  const style: CSSProperties = { position: 'fixed', width: TOOLTIP_WIDTH, zIndex: 10001 };

  switch (placement) {
    case 'right':
      style.left = rect.right + SPOTLIGHT_PADDING + TOOLTIP_GAP;
      style.top = rect.top + rect.height / 2;
      style.transform = 'translateY(-50%)';
      break;
    case 'left':
      style.left = rect.left - SPOTLIGHT_PADDING - TOOLTIP_GAP - TOOLTIP_WIDTH;
      style.top = rect.top + rect.height / 2;
      style.transform = 'translateY(-50%)';
      break;
    case 'bottom':
      style.left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
      style.top = rect.bottom + SPOTLIGHT_PADDING + TOOLTIP_GAP;
      break;
    case 'top':
      style.left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
      style.top = rect.top - SPOTLIGHT_PADDING - TOOLTIP_GAP;
      style.transform = 'translateY(-100%)';
      break;
  }

  return style;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TourStep({
  target,
  title,
  description,
  placement,
  stepIndex,
  totalSteps,
  showBack,
  onNext,
  onBack,
  onSkip,
}: TourStepProps) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const rafRef = useRef<number>(0);

  // Keep the rect in sync with layout changes / scroll / resize
  const updateRect = useCallback(() => {
    const r = getTargetRect(target);
    setRect(r);
  }, [target]);

  useEffect(() => {
    // Initial measurement
    updateRect();

    // Re-measure on scroll, resize, and on any transition that might shift layout
    const handleUpdate = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateRect);
    };

    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);

    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
      cancelAnimationFrame(rafRef.current);
    };
  }, [updateRect]);

  // Scroll target into view when step mounts
  useEffect(() => {
    const el = document.querySelector(target);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [target]);

  // If the target is not found, render nothing (graceful degradation)
  if (!rect) return null;

  const isLastStep = stepIndex === totalSteps - 1;

  // Spotlight hole dimensions (with padding)
  const holeLeft = rect.left - SPOTLIGHT_PADDING;
  const holeTop = rect.top - SPOTLIGHT_PADDING;
  const holeWidth = rect.width + SPOTLIGHT_PADDING * 2;
  const holeHeight = rect.height + SPOTLIGHT_PADDING * 2;

  const tooltipStyle = computeTooltipPosition(rect, placement);

  return (
    <>
      {/* Full-screen overlay with a transparent hole */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10000,
          pointerEvents: 'auto',
        }}
      >
        {/* Top band */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: holeTop,
            backgroundColor: 'rgba(10, 14, 23, 0.75)',
          }}
        />
        {/* Bottom band */}
        <div
          style={{
            position: 'absolute',
            top: holeTop + holeHeight,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(10, 14, 23, 0.75)',
          }}
        />
        {/* Left band (between top and bottom) */}
        <div
          style={{
            position: 'absolute',
            top: holeTop,
            left: 0,
            width: holeLeft,
            height: holeHeight,
            backgroundColor: 'rgba(10, 14, 23, 0.75)',
          }}
        />
        {/* Right band (between top and bottom) */}
        <div
          style={{
            position: 'absolute',
            top: holeTop,
            left: holeLeft + holeWidth,
            right: 0,
            height: holeHeight,
            backgroundColor: 'rgba(10, 14, 23, 0.75)',
          }}
        />
        {/* The transparent hole itself -- catches clicks through to the target */}
        <div
          style={{
            position: 'absolute',
            top: holeTop,
            left: holeLeft,
            width: holeWidth,
            height: holeHeight,
            borderRadius: 8,
            boxShadow: '0 0 0 2px rgba(212, 160, 23, 0.5)',
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* Tooltip */}
      <div
        role="dialog"
        aria-label={`Tour step ${stepIndex + 1} of ${totalSteps}: ${title}`}
        style={tooltipStyle}
        className="animate-fade-in rounded-lg border border-trunnion-gold-500/40 bg-trunnion-slate-800 p-4 shadow-glow-gold"
      >
        {/* Header */}
        <div className="mb-2 flex items-start justify-between">
          <h3 className="text-sm font-semibold text-trunnion-gold-500">{title}</h3>
          <button
            onClick={onSkip}
            className="ml-2 shrink-0 rounded p-0.5 text-trunnion-slate-400 hover:text-white transition-colors"
            aria-label="Skip tour"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Description */}
        <p className="mb-4 text-sm leading-relaxed text-trunnion-slate-200">
          {description}
        </p>

        {/* Footer: step counter + navigation */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-trunnion-slate-400">
            {stepIndex + 1} of {totalSteps}
          </span>

          <div className="flex items-center gap-2">
            {showBack && (
              <button
                onClick={onBack}
                className="flex items-center gap-1 rounded-md border border-trunnion-slate-600 bg-trunnion-slate-700 px-3 py-1.5 text-xs font-medium text-trunnion-slate-200 hover:bg-trunnion-slate-600 transition-colors"
              >
                <ChevronLeft className="h-3 w-3" />
                Back
              </button>
            )}
            <button
              onClick={onNext}
              className="flex items-center gap-1 rounded-md bg-trunnion-gold-500 px-3 py-1.5 text-xs font-bold text-trunnion-slate-950 hover:bg-trunnion-gold-400 transition-colors"
            >
              {isLastStep ? 'Finish' : 'Next'}
              {!isLastStep && <ChevronRight className="h-3 w-3" />}
            </button>
          </div>
        </div>

        {/* Skip link */}
        <div className="mt-3 text-center">
          <button
            onClick={onSkip}
            className="text-xs text-trunnion-slate-500 underline-offset-2 hover:text-trunnion-slate-300 hover:underline transition-colors"
          >
            Skip tour
          </button>
        </div>
      </div>
    </>
  );
}
